import { fetchData, setHeaderOptions } from "#/api/apiHelpers";

export type ArticleReview = {
  id: string;
  article_id: string;
  revision_id: string;
  status: string;
  notes?: string | null;
  reviewer_id?: string | null;
  section_id?: string | null;
  block_id?: string | null;
  created_at: string;
} & Record<string, unknown>;

export type ArticleReviewInsert = {
  id: string;
  article_id: string;
  revision_id: string;
  status: string;
  notes?: string | null;
  reviewer_id?: string | null;
  section_id?: string | null;
  block_id?: string | null;
  created_at?: string;
};

export type ArticleReviewUpdate = {
  id?: string;
  article_id?: string;
  revision_id?: string;
  status?: string;
  notes?: string | null;
  reviewer_id?: string | null;
  section_id?: string | null;
  block_id?: string | null;
  created_at?: string;
};

export type UpdateArticleReviewRequest = {
  status?: string;
  notes?: string | null;
  blockId?: string | null;
  sectionId?: string | null;
};

export type ArticleReviewQuery = {
  articleId?: string;
  revisionId?: string;
};

export type ArticleReviewArticleSummary = {
  articleId: string;
  reviewCount: number;
  lastReviewedAt?: string | null;
};

const BASE_ROUTE = "/article-reviews";
const ARTICLES_ROUTE = `${BASE_ROUTE}/articles`;

function buildQuery(params?: ArticleReviewQuery) {
  const query = new URLSearchParams();
  if (params?.articleId) {
    query.set("articleId", params.articleId);
  }
  if (params?.revisionId) {
    query.set("revisionId", params.revisionId);
  }

  const queryString = query.toString();
  return queryString ? `${BASE_ROUTE}?${queryString}` : BASE_ROUTE;
}

export async function listArticleReviews(
  params?: ArticleReviewQuery,
  options?: { signal?: AbortSignal }
): Promise<ArticleReview[]> {
  if (!params?.articleId && !params?.revisionId) {
    throw new Error("articleId or revisionId is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleReview[]>(buildQuery(params), {
    ...request,
    signal: options?.signal,
  });
}

export async function getArticleReviewById(
  id: string,
  options?: { signal?: AbortSignal }
): Promise<ArticleReview> {
  if (!id) {
    throw new Error("Article review id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleReview>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    {
      ...request,
      signal: options?.signal,
    }
  );
}

export async function listArticlesWithReviews(
  options?: { signal?: AbortSignal }
): Promise<ArticleReviewArticleSummary[]> {
  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleReviewArticleSummary[]>(ARTICLES_ROUTE, {
    ...request,
    signal: options?.signal,
  });
}

export async function updateArticleReview(
  id: string,
  payload: UpdateArticleReviewRequest
): Promise<ArticleReview> {
  if (!id) {
    throw new Error("Article review id is required");
  }

  const request = setHeaderOptions({
    method: "PATCH",
    body: payload,
  });

  return fetchData<ArticleReview>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}

export async function deleteArticleReview(
  id: string
): Promise<{ id: string }> {
  if (!id) {
    throw new Error("Article review id is required");
  }

  const request = setHeaderOptions({ method: "DELETE" });
  return fetchData<{ id: string }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}
